import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { Box, Icon, useEventCallback } from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField';
import {
  DatePicker as MuiDatePicker,
  DatePickerSlotProps,
  DatePickerSlots,
  PropsFromSlot,
  renderTimeViewClock,
} from '@mui/x-date-pickers';
import { DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import CalendarIcon from '@lmt/unicorn-shared/app/icons/calendar.svg?react';
import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import clsx from 'clsx';
import { format, isValid, startOfDay } from 'date-fns';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import styles from './DatePicker.module.scss';
import { getCurrentlyOpenedTabId } from '@lmt/unicorn-shared/app/features/navigation/operations';
// If we just set `format` of Material datepickers to e.g. 'f',
// it becomes impossible to edit it with keyboard (https://github.com/mui-org/material-ui-pickers/issues/2120).
// So we have to guess the format of the current locale.

const dateFormat = () =>
  localFormat(new Date(2222, 11, 23), 'P')
    .replace('2222', 'yyyy')
    .replace('12', 'MM')
    .replace('23', 'dd');
const dateTimeFormat = () =>
  dateFormat() +
  ' ' +
  localFormat(new Date(2222, 11, 23, 22, 15, 16), 'p')
    .replace('22', 'HH')
    .replace('10', 'hh')
    .replace('15', 'mm')
    .replace('16', 'ss')
    .replace(/pm/i, 'a');
const dateTimeWithSecondsFormat = () =>
  dateFormat() +
  ' ' +
  localFormat(new Date(2222, 11, 23, 22, 15, 16), 'pp')
    .replace('22', 'HH')
    .replace('10', 'hh')
    .replace('15', 'mm')
    .replace('16', 'ss')
    .replace(/pm/i, 'a');

export type DatePickerProps = {
  minDate?: Date;
  defaultValue?: Date | null;
  onDateChanged?: (newDate: Date | null) => void;
  containerClassName?: string;
  inputClassName?: string;
  value?: Date | null;
  error?: boolean;
  errorText?: string;
  placeholder?: string;
  testId?: string;
  allowToSelectYear?: boolean;
  disableKeyboardMode?: boolean;
  displaySeconds?: boolean;
  /*
Called when user typed something wrong (e.g. 'asd') and leaves the field (onBlur).
Form that owns this control shall account the error state for validation purposes.
When user corrects his input, `onDateChanged` will be called.
 */
  onInvalidDate?: () => void;
  isFormInput?: boolean;
  customText?: string;
  readonly?: boolean;
  disableInternalErrors?: boolean;
  endAdornment?: JSX.Element;
} & (
  | {
      withTime?: false | undefined;
    }
  | {
      withTime: true;
      /// ******
      /// Adjusting the time is important at least for different Filters (when you put this value in the URL)
      /// Set to true when you imply that user selects the time in the Clinic's timezone.
      /// But sometimes (e.g. in DataBlock inputs where we handle it differently) we might want to disable that
      /// ******
      doNotAdjustTimeAccordingToClinicTimezone?: boolean;
    }
);

interface CustomTextFieldProps
  extends Omit<PropsFromSlot<DatePickerSlots<Date>['textField']>, 'variant'> {
  ownerState?: any;
  variant: 'normal' | 'formInput';
  endAdornment?: JSX.Element;
}

export function DatePicker(props: DatePickerProps) {
  const {
    defaultValue,
    onDateChanged,
    value,
    error,
    errorText,
    containerClassName,
    minDate,
    placeholder,
    disableKeyboardMode,
    inputClassName,
    isFormInput = true,
    customText,
    readonly,
    disableInternalErrors = false,
    endAdornment,
    displaySeconds,
  } = props;

  const i18n = useTranslation();

  const [isInvalidDateError, setIsInvalidDateError] = useState(false);
  const isInvalidDateErrorRef = useRef(isInvalidDateError);
  isInvalidDateErrorRef.current = isInvalidDateError;

  // If set, this error is displayed instead of props.error.
  // Is used for Invalid Date error
  const [overriddenError, setOverriddenError] = useState<string | null>(null);
  const overriddenErrorRef = useRef<string | undefined>('');
  overriddenErrorRef.current = overriddenError ?? errorText;

  useEffect(
    function resetOverriddenErrorWhenValidationSucceeds() {
      if (!isInvalidDateError) {
        setOverriddenError(null);
      }
    },
    [isInvalidDateError],
  );
  const onBlur: TextFieldProps['onBlur'] = useCallback(() => {
    if (isInvalidDateErrorRef.current) {
      setOverriddenError(i18n.t('errors.invalid_date'));
    }
  }, []);

  const isUncontrolledMode = useMemo(() => value === undefined, []);
  const [internalDate, selectDate] = useState<Date | undefined | null>(
    defaultValue,
  );

  const selectedDate = useMemo(
    () => (isUncontrolledMode ? internalDate : value),
    [internalDate, value],
  );
  const callOnDateChanged = useEventCallback(
    (date: Date | null | undefined) => {
      if (!date) {
        onDateChanged?.(null);
        return;
      }
      if (props.withTime && !props.doNotAdjustTimeAccordingToClinicTimezone) {
        // date = convertFromCurrentTimezone(date);
      }
      onDateChanged?.(date);
    },
  );

  const handleDateChanged = useCallback(
    (newDate: Date | null) => {
      if (newDate == null) {
        setIsInvalidDateError(false);
        onDateChanged?.(newDate);
      } else if (isValid(newDate)) {
        setIsInvalidDateError(false);
        if (props.withTime) {
          isUncontrolledMode && selectDate(newDate);
          // in UIBlocks this function is called only when dialog is actually closed
          // so there's no need to check for isOpenRef.current === false
          //if (isOpenRef.current === false) {
          callOnDateChanged(newDate);
          //}
        } else {
          isUncontrolledMode && selectDate(newDate && startOfDay(newDate));
          onDateChanged?.(newDate && startOfDay(newDate));
        }
      } else {
        props.onInvalidDate?.();
        setIsInvalidDateError(!disableInternalErrors); //!disableErrors && true === !disableErrors
      }
    },
    [selectDate, onDateChanged, isUncontrolledMode],
  );
  const isOpenRef = useRef(false);
  const [tabId] = useState(getCurrentlyOpenedTabId);
  const onClose = useCallback(() => {
    isOpenRef.current = false;
    if (isUncontrolledMode) {
      // fixes Bug 45200: Patient list is empty
      // Repro:
      // 1. go to e.g. Audit page, open DatePicker in Filters
      // 2. switch to another tab (e.g. Patient List)
      // 3. problem: selected date gets added to PatientList filters
      if (tabId !== getCurrentlyOpenedTabId()) return;

      selectDate((date) => {
        callOnDateChanged(date);
        return date;
      });
    }
  }, [isUncontrolledMode, onDateChanged]);

  const onOpen = useCallback(() => {
    isOpenRef.current = true;
  }, []);

  const isError = !!overriddenError || !!error;
  const dateFormatMemo = useMemo(dateFormat, [i18n.i18n.language]);
  const dateTimeFormatMemo = useMemo(
    props.displaySeconds ? dateTimeWithSecondsFormat : dateTimeFormat,
    [props.displaySeconds, i18n.i18n.language],
  );

  const textFieldProps = useMemo(() => {
    return {
      placeholder,
      variant: props.isFormInput === false ? 'normal' : 'formInput',
      endAdornment: props.endAdornment,
      errorText: overriddenErrorRef.current,
      onBlur: onBlur,
    } as DatePickerSlotProps<Date, boolean>;
  }, [
    props.isFormInput,
    props.endAdornment,
    props.withTime,
    placeholder,
    overriddenErrorRef.current,
    dateTimeFormatMemo,
    dateFormatMemo,
    onBlur,
  ]);

  return (
    <>
      <div className={clsx(styles.container, containerClassName)}>
        {props.withTime ? (
          <MuiDateTimePicker
            reduceAnimations={true}
            // autoOk={false}
            format={dateTimeFormatMemo}
            value={
              selectedDate ??
              null /* if value is undefined, TODAY is displayed */
            }
            minDate={minDate}
            onOpen={onOpen}
            onClose={onClose}
            onChange={handleDateChanged}
            className={clsx(styles.input, inputClassName)}
            data-error={isError}
            data-test-id={props.testId}
            dayOfWeekFormatter={(date) => format(date, 'EEEEEE')}
            readOnly={readonly}
            slots={{
              textField: TextFieldComponent,
            }}
            slotProps={{
              layout: dialogProps,
              toolbar: { hidden: true },
              textField: textFieldProps as any,
            }}
            views={displaySeconds ? ['hours', 'minutes', 'seconds'] : undefined}
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderTimeViewClock,
              seconds: renderTimeViewClock,
            }}
          />
        ) : (
          <MuiDatePicker<Date>
            reduceAnimations={true}
            format={dateFormatMemo}
            value={
              selectedDate ??
              null /* if value is undefined, TODAY is displayed */
            }
            minDate={minDate}
            onOpen={onOpen}
            onClose={onClose}
            onChange={handleDateChanged}
            className={clsx(styles.input, inputClassName)}
            data-error={isError}
            data-test-id={props.testId}
            dayOfWeekFormatter={(date) => format(date, 'EEEEEE')}
            readOnly={readonly}
            slots={{
              textField: TextFieldComponent,
            }}
            slotProps={{
              textField: textFieldProps as any,
              layout: dialogProps,
            }}
          />
        )}
      </div>
    </>
  );
}

function TextFieldComponent(textFieldProps: CustomTextFieldProps) {
  const {
    id,
    label,
    InputProps: { ref: containerRef } = {},
    inputProps,
    // extracting `error`, 'focused', and `ownerState` as `input` does not support those props
    error,
    focused,
    size,
    ref,
    defaultValue,
    ownerState,
    value,
    helperText,
    variant,
    endAdornment,
    placeholder,
    ...other
  } = textFieldProps;

  const endAdornmentResult = (
    <>
      {endAdornment}
      <CalendarIcon
        className={styles['calendar-icon']}
        data-test-id={'calendar-icon'}
        onClick={
          (textFieldProps?.InputProps?.endAdornment as any)?.props?.children[1]
            ?.props?.children?.props?.onClick
        }
      />
    </>
  );

  return (
    <Box id={id} ref={containerRef}>
      <Input
        value={value as string}
        endAdornment={endAdornmentResult}
        variant={variant}
        placeholder={placeholder}
        {...inputProps}
        {...other}
      />
    </Box>
  );
}

const dialogProps = {
  className: styles.dialog,
};
const popoverProps = {
  className: styles.toolbar,
};
